import { AxiosError } from 'axios';
import T from '@data/text/commonTexts.json';
import type { ErrorResponse } from '@store/ReduxTypes';

type ApiErrorBody = Partial<ErrorResponse> & { detail?: unknown };

export function normalizeApiError(error: unknown): ErrorResponse {
  const axiosError = error as AxiosError<ApiErrorBody>;
  const data = axiosError.response?.data;
  const detail = typeof data?.detail === 'string' ? data.detail : undefined;
  const message =
    (typeof data?.message === 'string' && data.message) ||
    detail ||
    (error instanceof Error && error.message) ||
    T.errors.requestProcessing;

  return {
    message,
    detail,
    code: typeof data?.code === 'string' ? data.code : undefined,
    fields:
      data?.fields && typeof data.fields === 'object'
        ? Object.fromEntries(Object.entries(data.fields).filter(([, value]) => typeof value === 'string'))
        : undefined,
  };
}

export function getErrorMessage(error: any): string {
  const normalized = normalizeApiError(error);
  if (normalized.fields && Object.keys(normalized.fields).length > 0) {
    return Object.values(normalized.fields)[0];
  }
  if (normalized.code === 'invalid_or_expired_token') {
    return 'Ссылка для восстановления недействительна или истекла';
  }
  if (!error) return T.errors.requestProcessing;

  // Унифицированное извлечение текста ошибки
  let detailRaw: string | undefined;

  if ((error as AxiosError).isAxiosError && (error as AxiosError).response) {
    const data = (error as AxiosError<{ detail?: string; message?: string }>).response?.data;
    detailRaw = data?.detail || data?.message;
  } else {
    detailRaw =
      typeof error === 'string'
        ? error
        : error?.detail || error?.data?.detail || error?.message;
  }

  const detail = detailRaw?.toLowerCase().trim() || '';

  if (
    (error as AxiosError).code === 'ECONNABORTED' ||
    detail === 'network error' ||
    detail.includes('network error') ||
    detail.includes('timeout')
  ) {
    return T.errors.networkError;
  }

  const errorMap: { match: string; message: string }[] = [
    { match: 'email_already_exists', message: T.errors.emailExists },
    { match: 'policy_already_attached', message: T.errors.policyAttached },
    { match: 'policy_not_registered', message: T.errors.policyNotRegistered },
    { match: 'email, phone or policy already in use', message: T.errors.emailOrPhoneUsed },
    { match: 'invalid credentials', message: T.errors.invalidCredentials },
    { match: 'user not found', message: T.errors.userNotFound },
    { match: 'promo_code_not_found', message: T.errors.promoCodeNotFound },
  ];

  for (const { match, message } of errorMap) {
    if (detail.includes(match)) {
      return message;
    }
  }

  return T.errors.requestProcessing;
}
